import { config } from 'config';
import sortBy from 'lodash/sortBy';

import {
  fetchFeatureFlagAndExperimentAssignments,
  RuntimePlatform,
} from '@glossgenius/core-features-react-sdk';

import { EXPERIMENTS } from 'core-features-config/activeExeperiments';
import { FEATURE_FLAGS } from 'core-features-config/activeFeatureFlags';

import { TProfessional } from 'types/appointment';
import { TService } from 'types/service';

import {
  getMembershipsByBusinessToken,
  getPackagesByBusinessToken,
  getSalonBySlug,
  getServicesBySlug,
  getThemeBySlug,
} from '../api';
import { Site } from './getSiteFromReq';
import { getClient } from './httpClient/httpClient.config';

const prepareServices = (services: TService[], user: TProfessional) => {
  // A service is assigned to a professional if
  // - service has options: at least one option is in user assigned service
  // - service has no options: service is in user assigned service
  const filteredServices = services.filter((service) => {
    if (service.options?.length) {
      return service.options?.some((option) =>
        user.assigned_services.includes(option.guid),
      );
    }
    return user.assigned_services.includes(service.guid);
  });

  // A service can be assigned to a professional if at least one of its options is assigned
  // to that professional. So we need to filter out options that are not assigned to that professional
  const filteredServiceOptions = filteredServices.map((service) => {
    if (service.options) {
      return {
        ...service,
        options: service.options?.filter((option) =>
          user.assigned_services.includes(option.guid),
        ),
      };
    }
    return service;
  });

  return sortBy(filteredServiceOptions, ['ordering', 'name']);
};

export const getSalonConfiguration = async (site: Site) => {
  const salonPromise = getSalonBySlug(site.value).catch((e: unknown) => {
    const error = e as Error;

    console.error(
      `[getSalonConfiguration] Unable to fetch salon from ${site.value} due to error: ${error.message}`,
    );
    throw Error(`Provided ${site.value} slug does not exist`);
  });

  const salon = await salonPromise;

  const postSenderForCoreFeatures = (url: string, data: any) => {
    // We use a static url, and ignore the one provided, because it will change
    // when we create and use a new endpoint that will be used by default.
    // By default, the React SDK will use the endpoint that hydrates targeting
    // properties from the server side, but we don't want that here, since we
    // are doing SSR.
    return getClient().post(
      `${config.apiUrlBase as string}/v1/features/client/evaluate`,
      data,
    );
  };

  const coreFeaturesBackendDataPromise =
    fetchFeatureFlagAndExperimentAssignments({
      businessGuid: salon.guid,
      userGuid: salon.owner_guid,
      businessToken: salon.business_token,
      userToken: 'no user token',
      experiments: EXPERIMENTS,
      featureFlags: FEATURE_FLAGS,
      postSender: postSenderForCoreFeatures,
      targetedRuntime: { platform: RuntimePlatform.WEB },
      businessTargetingProperties: {
        legacy_slug: salon.slug,
      },
    });

  // TODO: guid is needed for BE to check feature flag for the salon.
  // Remove guid when cleaning up OPTIMIZELY_FEATURES.SERVICE_OPTION
  const servicesPromise = getServicesBySlug(site.value, salon.guid).catch(
    (e: unknown) => {
      const error = e as Error;

      console.error(
        `[getSalonConfiguration] Unable to fetch services from ${site.value} due to error: ${error.message}`,
      );
      return {
        isError: true,
      };
    },
  );

  const themePromise = getThemeBySlug(site.value).catch((e: unknown) => {
    const error = e as Error;

    console.error(
      `[getSalonConfiguration] Unable to fetch theme from ${site.value} due to error: ${error.message}`,
    );
    throw error;
  });

  const coreFeaturesBackendData = await coreFeaturesBackendDataPromise;

  const isPackagesEnabled = Boolean(
    coreFeaturesBackendData.featureFlagAssignments[FEATURE_FLAGS.PACKAGES_PAGE],
  );

  const packagesPromise = getPackagesByBusinessToken(
    salon.business_token,
    isPackagesEnabled,
  ).catch((e: unknown) => {
    const error = e as Error;

    console.error(
      `[getSalonConfiguration] Unable to fetch packages from ${site.value} due to error: ${error.message}`,
    );

    return [];
  });

  const isMembershipsEnabled = Boolean(
    coreFeaturesBackendData.featureFlagAssignments[
      FEATURE_FLAGS.SELL_MEMBERSHIPS_ON_BOOKING_SITES
    ],
  );

  const membershipsPromise = getMembershipsByBusinessToken(
    salon.business_token,
    isMembershipsEnabled,
  ).catch((e: unknown) => {
    const error = e as Error;

    console.error(
      `[getSalonConfiguration] Unable to fetch memberships from ${site.value} due to error: ${error.message}`,
    );

    return [];
  });

  const [services, themeVariant, packages, memberships] = await Promise.all([
    servicesPromise,
    themePromise,
    packagesPromise,
    membershipsPromise,
  ]);

  return {
    publicUser: {
      ...salon,
      users: salon.users.map((user) => ({
        ...user,
        services: Array.isArray(services)
          ? prepareServices(services, user)
          : [],
      })),
      ...(!Array.isArray(services) &&
        services.isError && { services_error: true }),
    },
    packages,
    memberships,
    theme: themeVariant,
    coreFeaturesBackendData,
  };
};
